import { DEFAULT_SURFACED_IN } from './defineFeature.js';
import { FeatureType } from './types.js';
/**
 * Resolve `surfacedIn` to the explicit list, falling back to
 * DEFAULT_SURFACED_IN when the def omits it.
 */
function surfacedIn(def) {
    return def.surfacedIn ?? DEFAULT_SURFACED_IN;
}
/**
 * Type guards. The point is to narrow without `as` casts so call sites can
 * stay free of the `any`/`as never` escape hatches CLAUDE.md prohibits.
 */
export const isBooleanDef = (d) => d.type === FeatureType.Boolean;
export const isStringDef = (d) => d.type === FeatureType.String;
export const isNumberDef = (d) => d.type === FeatureType.Number;
export const isMarkdownDef = (d) => d.type === FeatureType.Markdown;
export const isMultiSelectDef = (d) => d.type === FeatureType.MultiSelect;
export const isJsonDef = (d) => d.type === FeatureType.Json;
/**
 * Lookup an option config on a multiselect feature by propName + value.
 * Returns undefined if the feature isn't a multiselect or the value isn't
 * a known option.
 */
export function getOption(defs, propName, value) {
    const def = defs.find((d) => d.propName === propName);
    if (!def || !isMultiSelectDef(def))
        return undefined;
    const options = def.options;
    return options[value];
}
/**
 * True if the feature applies to the given area. Hides the `'all'` vs `[]`
 * wildcard duality from callers, and treats `surfacedIn` as implying
 * applicability so a feature whose value is defined at one scope but
 * overridable at another (e.g. agent-defined, brand-overridable) is still
 * visible to the rollup for the override scope.
 */
export function isApplicableTo(def, area) {
    if (def.areas === 'all' || def.areas.includes(area))
        return true;
    return surfacedIn(def).includes(area);
}
/**
 * True if the feature is surfaced in the given management UI scope-tab.
 * Used by the management-api to gate which features the application/brand
 * tabs expose for override. See FeatureDefBase.surfacedIn.
 */
export function isSurfacedIn(def, area) {
    return surfacedIn(def).includes(area);
}
/**
 * Normalize def.areas to the storage shape: empty array means "all areas",
 * matching the legacy Features.areas convention.
 */
export function areasForStorage(def) {
    return def.areas === 'all' ? [] : [...def.areas];
}
